import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Divider,
  IconButton,
  CircularProgress,
  LinearProgress,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useParams, useNavigate } from "react-router-dom";

import { useConversations } from "../../hooks/useConversations";
import { useConversationDetails } from "../../hooks/useConversationDetails";
import ConversationDetails from "../../components/ConversationDetails";
import Placeholder from "../../components/Placeholder";
import MessageHistory from "../../components/MessageHistory";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { useSocketContext } from "../../context/SocketContext";
import { getConversationMessagesById, deleteConversationMessage } from "../../api/client";

// helper consistente com a tela principal
const getStatusLabelFromCode = (status) => {
  const statusMap = {
    0: "Pendente",
    1: "Em Atendimento",
    2: "Concluída",
    3: "Contato Futuro",
    4: "Perdida",
    5: "Cancelada",
  };

  // garante que a chave é numérica
  const key = typeof status === "string" ? Number(status) : status;
  return statusMap[key] ?? "Desconhecido";
};

const CallPopup = () => {
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesPageState, setMessagesPageState] = useState({
    hasMore: false,
    lastMessageId: null,
    isInitialLoaded: false,
    isLoadingMore: false,
  });

  const [searchTerm] = useState("");
  const [filters] = useState([]);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 500);
  const debouncedFilters = useDebouncedValue(filters, 2000);

  const [initialLoading, setInitialLoading] = useState(true);

  const { id } = useParams();
  const navigate = useNavigate();
  const socket = useSocketContext();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useTheme();

  const [showConversationList] = useState(false);
  const [showMessageHistory, setShowMessageHistory] = useState(isMobile ? false : true);
  const [showConversationDetails, setShowConversationDetails] = useState(true);

  const messagesSyncInFlightRef = useRef(false);
  const syncLatestConversationMessagesById = useCallback(
    async (conversationId, { limit = 50 } = {}) => {
      if (!conversationId) return;
      if (messagesSyncInFlightRef.current) return;
      messagesSyncInFlightRef.current = true;
      try {
        const payload = await getConversationMessagesById(String(conversationId), {
          limit,
          returnRaw: true,
        });
        const list = Array.isArray(payload?.messages)
          ? payload.messages
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        setMessages((prev) => {
          const map = new Map((prev || []).map((m) => [m.id, m]));

          const mergeKeepingLocalMedia = (prevMsg, incoming) => {
            if (!prevMsg) return incoming;
            const merged = { ...prevMsg, ...incoming };
            const keepIfMissing = ["image", "audio", "video", "file", "document", "location"];
            keepIfMissing.forEach((k) => {
              if (
                (merged[k] === null || merged[k] === undefined || merged[k] === "") &&
                prevMsg[k]
              ) {
                merged[k] = prevMsg[k];
              }
            });
            return merged;
          };

          for (const msg of list) {
            if (!msg || msg.id == null) continue;
            const existing = map.get(msg.id);
            map.set(msg.id, mergeKeepingLocalMedia(existing, msg));
          }

          return Array.from(map.values());
        });
      } catch (err) {
        console.error("Erro ao sincronizar mensagens (CallPopup):", err);
      } finally {
        messagesSyncInFlightRef.current = false;
      }
    },
    []
  );

  // carrega detalhes
  const {
    data: conversationDetails,
    isLoading: isLoadingDetails,
    refetch: refetchConversationDetails,
  } = useConversationDetails(selectedConversationId);

  // console.log("Conversation Details Data:", conversationDetails);

  // carrega conversas (apenas para sincronizar loading; não exibimos lista aqui)
  const { isLoading, isError } = useConversations(
    debouncedSearchTerm,
    debouncedFilters,
    { default_date_window_days: 30 }
  );

  // Normaliza payload de detalhes assim que chegar
  useEffect(() => {
    if (!conversationDetails?.data) return;

    const d = conversationDetails.data;

    const normalized = {
      id: d.id,
      status: d.status,
      statusLabel: getStatusLabelFromCode(d.status),
      projectId: d.projectId ?? null,
      channelId: d.channelId ?? null,
      typeId: d.typeId ?? null,
      typeType: d.typeType ?? null,
      subtype1: d.subtype1 ?? null,
      subtype2: d.subtype2 ?? null,
      subtype3: d.subtype3 ?? null,
      userId: d.userId ?? null,
      potential: d.potential ?? null,
      nf: d.nf ?? null,

      clientId: d.clientId ?? null,
      nome: d.client?.name ?? "",
      email: d.client?.email ?? "",
      celular: d.client?.phone ?? "",
      cep: d.client?.zip ?? "",
      endereco: d.client?.address ?? "",
      numero: d.client?.addressNumber ?? "",
      complemento: d.client?.addressComplement ?? "",
      bairro: d.client?.neighborhood ?? "",
      cidade: d.client?.city ?? d.client?.locality ?? null,
      uf: d.client?.state ?? null,
      clientPotential: d.client?.potential ?? null,

      channel: d.channel ?? undefined,

      // ✅ produtos e histórico vindos da API
      products: Array.isArray(d.products) ? d.products : [],
      history: Array.isArray(d.history) ? d.history : [],
    };

    setSelectedConversation((prev) => ({
      ...(prev || {}),
      ...normalized,
    }));
  }, [conversationDetails]);

  // Carrega mensagens da conversa com paginação (mais novas primeiro)
  useEffect(() => {
    const loadInitialMessages = async () => {
      if (!selectedConversationId) return;
      setMessagesPageState((prev) => ({
        ...prev,
        isInitialLoaded: false,
        isLoadingMore: true,
      }));
      try {
        const payload = await getConversationMessagesById(selectedConversationId, {
          limit: 50,
          returnRaw: true,
        });
        const list = Array.isArray(payload.messages)
          ? payload.messages
          : Array.isArray(payload.data)
            ? payload.data
            : [];

        setMessages(list);

        const pagination = payload.pagination || {};
        setMessagesPageState({
          hasMore: Boolean(pagination.hasMore),
          lastMessageId: pagination.lastMessageId ?? null,
          isInitialLoaded: true,
          isLoadingMore: false,
        });
      } catch (err) {
        console.error("Erro ao carregar mensagens no popup:", err);
        setMessagesPageState((prev) => ({
          ...prev,
          isLoadingMore: false,
          isInitialLoaded: true,
        }));
      }
    };

    if (selectedConversationId) {
      loadInitialMessages();
    } else {
      setMessages([]);
      setMessagesPageState({
        hasMore: false,
        lastMessageId: null,
        isInitialLoaded: false,
        isLoadingMore: false,
      });
    }
  }, [conversationDetails]);

  // Carrega conversa pelo ID da URL
  useEffect(() => {
    if (id) setSelectedConversationId(id);
  }, [id]);

  // Identifica o primeiro carregamento com base no hook de conversas
  useEffect(() => {
    setInitialLoading(Boolean(isLoading));
  }, [isLoading]);

  const handleShowDetails = () => {
    if (isMobile) setShowMessageHistory(false);
    setShowConversationDetails(true);
  };

  const handleSendMessage = (payload) => {
    if (!selectedConversation) return;
    const { message } = payload || {};
    if (!message) return;

    setMessages((prev) => {
      const lastId = prev.length ? prev[prev.length - 1].id : 0;
      return [
        ...prev,
        {
          id: lastId + 1,
          text: message,
          sender: "Atendente",
          timestamp: new Date().toISOString(),
        },
      ];
    });
  };

  const handleDeleteMessage = useCallback(
    async (messageId) => {
      if (!selectedConversationId || messageId == null) return;
      const mid = typeof messageId === "number" ? messageId : Number(messageId);
      if (!Number.isFinite(mid)) return;
      try {
        await deleteConversationMessage(selectedConversationId, mid);
        setMessages((prev) => (prev || []).filter((m) => Number(m.id) !== mid));
      } catch (e) {
        const msg =
          (typeof e?.message === "string" && e.message) ||
          (Array.isArray(e?.message) && e.message[0]) ||
          "Não foi possível apagar a mensagem.";
        window.alert(msg);
        throw e;
      }
    },
    [selectedConversationId]
  );

  const handleEndConversation = () => {
    alert("Solicitação encerrada");
    setSelectedConversation(null);
    setMessages([]);
    setMessagesPageState({
      hasMore: false,
      lastMessageId: null,
      isInitialLoaded: false,
      isLoadingMore: false,
    });
    navigate("/chat");
  };

  const containerStyle = {
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 1px 4px rgba(0, 0, 0, 0.5)"
        : "0 1px 4px rgba(0, 0, 0, 0.1)",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "8px",
    overflow: "hidden",
  };

  // socket.io: refetch leve apontando para o ID aberto
  useEffect(() => {
    if (!socket) return;

    const refreshIfSame = (changedId) => {
      if (String(changedId) === String(selectedConversationId)) {
        syncLatestConversationMessagesById(String(changedId), { limit: 50 });
      }
    };

    socket.on("request", refreshIfSame);
    socket.on("message", refreshIfSame);
    socket.on("messagesReload", refreshIfSame);

    return () => {
      socket.off("request", refreshIfSame);
      socket.off("message", refreshIfSame);
      socket.off("messagesReload", refreshIfSame);
    };
  }, [socket, selectedConversationId, syncLatestConversationMessagesById]);

  // Reconciliação por polling quando a aba está visível (fallback quando socket falha).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!selectedConversationId) return;

    const runSync = () => {
      if (document.visibilityState !== "visible") return;
      syncLatestConversationMessagesById(selectedConversationId, { limit: 50 });
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") runSync();
    };

    const timer = setInterval(runSync, 12000);
    window.addEventListener("focus", runSync);
    document.addEventListener("visibilitychange", handleVisibility);

    // Sync imediato ao abrir.
    runSync();

    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", runSync);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [selectedConversationId, syncLatestConversationMessagesById]);

  if (initialLoading) {
    return (
      <Box display="flex" gap={1} height="98%" p={0}>
        <Skeleton variant="rectangular" width="25%" height="100%" sx={{ minWidth: 200, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography color="error">Erro ao carregar as conversas.</Typography>
      </Box>
    );
  }

  // console.log("Selected Conversation in CallPopup:", selectedConversation);

  return (
    <Box display="flex" height="98%" p={0} gap={1}>
      {/* Detalhes da Conversa */}
      {showConversationDetails && selectedConversation && (
        <Box
          width={isMobile ? "100%" : "25%"}
          bgcolor={theme.palette.background.paper}
          display="flex"
          flexDirection="column"
          height="100%"
          sx={containerStyle}
        >
          <ConversationDetails
            full
            selectedConversation={selectedConversation}
            onSelectedConversationUpdate={(updatedFields) => {
              setSelectedConversation((prev) => ({
                ...(prev || {}),
                ...updatedFields,
              }));
            }}
            onRefetchConversationDetails={refetchConversationDetails}
          />
        </Box>
      )}

      {/* Histórico de Mensagens */}
      {showMessageHistory && (
        <Box
          width={
            !selectedConversation
              ? showConversationList
                ? "75%"
                : "100%"
              : showConversationList && showConversationDetails
              ? "50%"
              : showConversationList || showConversationDetails
              ? "75%"
              : "100%"
          }
          bgcolor={theme.palette.background.paper}
          display="flex"
          flexDirection="column"
          height="100%"
          sx={containerStyle}
        >
          <Box display="flex" justifyContent="center" alignItems="center" p={1}>
            {selectedConversation && (
              <Box display="flex" alignItems="center" gap={1}>
                <Box display="flex" flexDirection="column">
                  <Typography variant="h6" sx={{ fontWeight: "bold", lineHeight: 1.2 }}>
                    {`Protocolo: ${selectedConversation?.id || "N/A"}`}
                  </Typography>
                </Box>
              </Box>
            )}

            {selectedConversation && !showConversationDetails && (
              <IconButton onClick={handleShowDetails}>
                <MenuIcon />
              </IconButton>
            )}
          </Box>
          <Divider />

          {isLoadingDetails && (
            <Box width="100%" position="relative">
              <LinearProgress sx={{ width: "100%", position: "absolute", top: 0 }} />
            </Box>
          )}

          {selectedConversation ? (
            <MessageHistory
              messages={messages}
              selectedConversation={selectedConversation}
              onSendMessage={handleSendMessage}
              onEndConversation={handleEndConversation}
              hasMoreMessages={messagesPageState.hasMore}
              isLoadingMoreMessages={messagesPageState.isLoadingMore}
              onLoadMoreMessages={async () => {
                if (!selectedConversationId) return;
                if (!messagesPageState.hasMore || messagesPageState.isLoadingMore) return;

                if (!Array.isArray(messages) || messages.length === 0) return;
                const sortedByIdAsc = [...messages].sort((a, b) => {
                  const idA = typeof a.id === "number" ? a.id : Number(a.id) || 0;
                  const idB = typeof b.id === "number" ? b.id : Number(b.id) || 0;
                  return idA - idB;
                });
                const oldest = sortedByIdAsc[0];
                if (!oldest?.id) return;

                setMessagesPageState((prev) => ({ ...prev, isLoadingMore: true }));
                try {
                  const payload = await getConversationMessagesById(selectedConversationId, {
                    limit: 50,
                    beforeMessageId: oldest.id,
                    returnRaw: true,
                  });
                  const list = Array.isArray(payload.messages)
                    ? payload.messages
                    : Array.isArray(payload.data)
                      ? payload.data
                      : [];

                  setMessages((prev) => {
                    const existingIds = new Set((prev || []).map((m) => m.id));
                    const merged = [...prev];
                    for (const msg of list) {
                      if (!existingIds.has(msg.id)) merged.push(msg);
                    }
                    return merged;
                  });

                  const pagination = payload.pagination || {};
                  setMessagesPageState((prev) => ({
                    ...prev,
                    hasMore: Boolean(pagination.hasMore),
                    lastMessageId: pagination.lastMessageId ?? prev.lastMessageId,
                    isLoadingMore: false,
                  }));
                } catch (err) {
                  console.error("Erro ao carregar mensagens antigas no popup:", err);
                  setMessagesPageState((prev) => ({ ...prev, isLoadingMore: false }));
                }
              }}
              onDeleteMessage={handleDeleteMessage}
            />
          ) : (
            <Placeholder />
          )}
        </Box>
      )}
    </Box>
  );
};

export default CallPopup;
